import React, { useState, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Edit, 
  Trash2, 
  Copy, 
  Save, 
  Clock,
  User,
  MoreHorizontal,
  Check,
  X
} from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DayShift, Employee } from '@/types';
import { format, isValid } from 'date-fns';
import { cn } from '@/lib/core/utils';
import { getShiftStatusStyles, StyleVariant } from '@/lib/scheduling/shift-status-styles';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';

interface EditableShiftCardProps {
  shift: DayShift;
  employee?: Employee;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onSaveAsTemplate: (name: string, description?: string) => void;
  className?: string;
  // Pre-calculated status from unified service
  status: ShiftStatus;
}

const EditableShiftCard: React.FC<EditableShiftCardProps> = ({
  shift,
  employee,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onCopy,
  onSaveAsTemplate,
  className = "",
  status,
}) => {
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isHovered, setIsHovered] = useState(false);

  // Safe date parsing with comprehensive error handling
  const safeParseDate = (dateValue: unknown): Date => {
    try {
      if (!dateValue) return new Date();
      const date = new Date(dateValue as string);
      return isValid(date) ? date : new Date();
    } catch (error) {
      console.warn('Error parsing date:', dateValue, error);
      return new Date();
    }
  };

  const safeFormat = (date: Date, formatString: string): string => {
    try {
      return isValid(date) ? format(date, formatString) : '--:--';
    } catch (error) {
      console.warn('Error formatting date:', date, formatString, error);
      return '--:--';
    }
  };

  const startTime = safeParseDate(shift.start_time);
  const endTime = safeParseDate(shift.end_time);

  // Safe duration calculation with error boundaries
  const duration = useMemo(() => {
    try {
      if (!isValid(startTime) || !isValid(endTime)) return 0;
      const durationMs = endTime.getTime() - startTime.getTime();
      if (isNaN(durationMs) || durationMs < 0) return 0;
      return Math.round(durationMs / (1000 * 60 * 60 * 100)) / 100; // Hours with 2 decimal places
    } catch (error) {
      console.warn('Error calculating duration:', error);
      return 0;
    }
  }, [startTime, endTime]);

  // Use the pre-calculated status passed as prop
  const statusStyles = getShiftStatusStyles(status, StyleVariant.COMPACT);

  const handleSaveAsTemplate = useCallback(() => {
    if (!templateName.trim()) return;
    
    onSaveAsTemplate(templateName.trim(), templateDescription.trim() || undefined);
    setShowTemplateDialog(false);
    setTemplateName('');
    setTemplateDescription('');
  }, [templateName, templateDescription, onSaveAsTemplate]);

  const handleCloseTemplateDialog = useCallback(() => {
    setShowTemplateDialog(false);
    setTemplateName('');
    setTemplateDescription('');
  }, []);

  return (
    <>
      <Card 
        className={cn(
          "relative transition-all duration-200 cursor-pointer hover:shadow-sm",
          selected && "ring-2 ring-blue-500 bg-blue-50/50",
          statusStyles.background,
          statusStyles.border,
          className
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          e.preventDefault();
          onSelect(!selected);
        }}
      >
        <CardContent className="p-1.5">
          <div className="flex items-start gap-1.5">
            {/* Selection checkbox */}
            <div className="mt-0.5">
              <Checkbox
                checked={selected}
                onCheckedChange={onSelect}
                onClick={(e) => e.stopPropagation()}
                aria-label="Select shift"
                className="h-3 w-3"
              />
            </div>

            {/* Shift info - compact layout */}
            <div className="flex-1 min-w-0 space-y-1">
              {/* Time */}
              <div className="flex items-center gap-1 text-xs">
                <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                <span className="font-mono text-xs truncate">
                  {safeFormat(startTime, 'HH:mm')}-{safeFormat(endTime, 'HH:mm')}
                </span>
              </div>

              {/* Employee and duration */}
              <div className="flex items-center gap-1 text-xs">
                <User className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                <span className={cn(
                  "truncate",
                  employee ? "text-foreground" : "text-muted-foreground italic"
                )}>
                  {employee?.name || 'Unassigned'}
                </span>
                <span className="text-muted-foreground text-xs flex-shrink-0 ml-auto">
                  {duration}h
                </span>
              </div>

              {/* Status badge - show pre-calculated status */}
              <div className="flex justify-start">
                <Badge
                  variant="outline"
                  className={cn("text-xs py-0 px-1 h-4", statusStyles.text, statusStyles.background, statusStyles.border)}
                >
                  {status}
                </Badge>
              </div>
            </div>

            {/* Actions - single dropdown */}
            <div className={cn(
              "flex items-center transition-opacity duration-200",
              !isHovered && !selected && "opacity-30"
            )}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-5 w-5 p-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36">
                  <DropdownMenuItem onClick={onEdit}>
                    <Edit className="h-3 w-3 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onCopy}>
                    <Copy className="h-3 w-3 mr-2" />
                    Copy
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowTemplateDialog(true)}>
                    <Save className="h-3 w-3 mr-2" />
                    Template
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={onDelete}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-3 w-3 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save as Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Save as Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Template preview */}
            <div className="bg-muted/50 p-3 rounded-md text-sm">
              <div className="font-medium">Template Preview:</div>
              <div className="text-muted-foreground mt-1">
                Duration: {duration}h ({format(startTime, 'HH:mm')} - {format(endTime, 'HH:mm')})
              </div>
              {employee && (
                <div className="text-muted-foreground">
                  Employee: {employee.name}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Morning Shift"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-description">Description (optional)</Label>
              <Textarea
                id="template-description"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Optional description for this template"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseTemplateDialog}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAsTemplate}
              disabled={!templateName.trim()}
            >
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditableShiftCard;